import { Gauge } from 'lucide-react';

import { MetaBlock } from '@/components/channels/MetaBlock';
import { useLocalPotential } from '@/hooks/useLocalPotential';
import { useShop } from '@/hooks/useShop';
import { formatCurrency } from '@/lib/currency';

const GAUGE_GRAD_ID = 'channelPotentialGaugeGrad';

/**
 * Small decorative gauge for the per-channel "Local Potential Score" boost
 * (ported from the fresh-dashboard design). Purely visual — a third-filled ring
 * with the gauge glyph; the boost figure is shown alongside it.
 */
export function PotentialGauge() {
	const size = 48;
	const stroke = 5;
	const r = (size - stroke) / 2;
	const c = 2 * Math.PI * r;
	const offset = c - (1 / 3) * c;
	return (
		<div className="relative shrink-0" style={{ width: size, height: size }}>
			<svg width={size} height={size}>
				<g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
					<circle cx={size / 2} cy={size / 2} r={r} stroke="hsl(var(--muted))" strokeWidth={stroke} fill="none" />
					<circle
						cx={size / 2}
						cy={size / 2}
						r={r}
						stroke={`url(#${GAUGE_GRAD_ID})`}
						strokeWidth={stroke}
						strokeLinecap="round"
						fill="none"
						strokeDasharray={c}
						strokeDashoffset={offset}
					/>
				</g>
				<defs>
					<linearGradient id={GAUGE_GRAD_ID} x1="0" y1="0" x2="1" y2="1">
						<stop offset="0%" stopColor="hsl(var(--primary))" />
						<stop offset="100%" stopColor="hsl(var(--action))" />
					</linearGradient>
				</defs>
			</svg>
			<div className="absolute inset-0 flex items-center justify-center text-primary">
				<Gauge className="h-4 w-4" />
			</div>
		</div>
	);
}

/**
 * The real Local Potential uplift for a channel, read live from the resolver
 * (`weights.json` via `groups[].items[].uplift`) — a channel's `id` IS the LP
 * item id, so the lookup is direct. Returns null while the score is loading or
 * for channels outside the score model (e.g. product-locator). Local Ads is the
 * one dynamic item, so callers render its formula instead of a single number.
 */
export function useChannelUplift(channelId) {
	const { groups } = useLocalPotential();
	if (!groups) return null;
	for (const group of groups) {
		const item = group.items.find((i) => i.id === channelId);
		if (item) return item.uplift;
	}
	return null;
}

/**
 * The "Local Potential Score" aside block on a channel's detail / manage page —
 * the gauge plus the channel's real uplift. Local Ads shows its dynamic formula.
 * Renders nothing for channels outside the score model (so the block only
 * appears where it has a number to show).
 */
export function ChannelPotentialBoost({ channelId }) {
	const uplift = useChannelUplift(channelId);
	// Local Ads is the one boost expressed per unit of spend, so it needs the
	// shop's currency — the rest are plain percentages.
	const { shop } = useShop();
	const isLocalAds = channelId === 'local-ads';
	if (!isLocalAds && uplift == null) return null;
	return (
		<MetaBlock label="Local Potential Score">
			<div className="flex items-center gap-3">
				<PotentialGauge />
				<div className="min-w-0">
					{isLocalAds ? (
						<>
							<div className="text-base font-bold tabular-nums text-foreground">
								+2% per {formatCurrency(1, shop?.currency)}/day
							</div>
							<div className="text-xs leading-tight text-muted-foreground">Up to +20% boost</div>
						</>
					) : (
						<>
							<div className="text-base font-bold tabular-nums text-foreground">+{uplift}%</div>
							<div className="text-xs leading-tight text-muted-foreground">Boost when enabled</div>
						</>
					)}
				</div>
			</div>
		</MetaBlock>
	);
}
